import { useEffect, useState, useCallback } from "react";
import { executeCode, executeTestCases, formatCode } from "../../../../apis/CodeExecutionApis";
import { submitCodingSolution, fetchTopicSubmissions } from "../../../../apis/SubmissionApis";
import { useAuth } from "../../../Authentication/AuthContext";
import { useToastHelpers } from "../../../Toasts/ToastUtils";
import AuthPromptModal from "../../../Authentication/AuthPromptModal";
import { readCachedCode, writeCachedCode } from "../../../../utils/codingCodeCache";
import { LANGUAGE_VERSIONS } from "../../../../constants/codeExecution";

const CodingView = ({ topic }) => {
  const coding = topic?.codingProblem || null;
  const { user, isAuthenticated } = useAuth();
  const { successToast, errorToast } = useToastHelpers();

  const [language, setLanguage] = useState("go");
  const [code, setCode] = useState("");
  const [isRunning, setIsRunning] = useState(false);
  const [isTestRunning, setIsTestRunning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormatting, setIsFormatting] = useState(false);
  const [activeTab, setActiveTab] = useState("problem");
  const [testMode, setTestMode] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [customInput, setCustomInput] = useState("");
  const [customOutput, setCustomOutput] = useState("");
  const [customError, setCustomError] = useState("");
  const [activeTestTab, setActiveTestTab] = useState("visible"); // visible, custom, hidden

  // Submission state
  const [submissions, setSubmissions] = useState([]);
  const [canSubmit, setCanSubmit] = useState(true);
  const [attemptsRemaining, setAttemptsRemaining] = useState(3);
  const [bestScore, setBestScore] = useState(0);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [expandedSubmissionId, setExpandedSubmissionId] = useState(null);

  // Fetch existing submissions on mount
  const loadSubmissions = useCallback(async () => {
    if (!isAuthenticated || !topic?.id) return;

    try {
      const data = await fetchTopicSubmissions(topic.id, user?.token);
      setSubmissions(data.submissions || []);
      setCanSubmit(data.canSubmit);
      setAttemptsRemaining(data.attemptsAllowed - data.attemptsUsed);
      setBestScore(data.bestScore || 0);
    } catch (err) {
      console.error("Failed to load submissions:", err);
    }
  }, [isAuthenticated, topic?.id, user?.token]);

  // ✅ Load cached code (topicId + language), fallback to boilerplate
  useEffect(() => {
    if (!coding?.boilerplate) return;

    const langs = Object.keys(coding.boilerplate);
    if (!langs.includes(language)) {
      setLanguage(langs[0]);
      return;
    }

    const cached = readCachedCode(topic?.id, language);
    if (cached !== null) {
      setCode(cached);
      return;
    }

    setCode(coding.boilerplate[language] || "");
  }, [coding, language, topic?.id]);

  // Load submissions on mount
  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions]);

  if (!coding) {
    return <p className="text-gray-500">Coding problem not available</p>;
  }

  const languages = Object.keys(coding.boilerplate || {});

  // ✅ Run visible test cases
  const handleRunTestCases = async () => {
    writeCachedCode(topic?.id, language, code);
    setIsTestRunning(true);
    setTestResults(null);

    try {
      // Prepare visible test cases with id, input, expected
      const visibleTestCases = (coding.testCases || []).map((tc, idx) => ({
        id: `visible_test_${idx + 1}`,
        input: tc.input || "",
        expected: tc.expected || "",
      }));

      // Prepare hidden test cases with id, input, expected
      const hiddenTestCases = (coding.hiddenTestCases || []).map((tc, idx) => ({
        id: `hidden_test_${idx + 1}`,
        input: tc.input || "",
        expected: tc.expected || "",
      }));

      // Combine all test cases
      const allTestCases = [...visibleTestCases, ...hiddenTestCases];

      const result = await executeTestCases({
        language,
        version: LANGUAGE_VERSIONS[language] || "1.0.0",
        code,
        testCases: allTestCases,
      });

      setTestResults(result);
      setTestMode(true);
    } catch (err) {
      console.error("Test execution failed:", err);
      errorToast("Failed to run tests: " + err.message);
    } finally {
      setIsTestRunning(false);
    }
  };

  // ✅ Submit code to backend
  const handleSubmitCode = async () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }

    if (!canSubmit) {
      errorToast("No attempts remaining");
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await submitCodingSolution(
        { topicId: topic.id, code, language },
        user?.token
      );

      // Update state with submission result
      setTestResults(result);
      setTestMode(true);
      setAttemptsRemaining(result.attemptsRemaining);
      setCanSubmit(result.attemptsRemaining > 0);

      // Reload submissions
      await loadSubmissions();

      const passed = result.passedTests === result.totalTests;
      if (passed) {
        successToast(`All tests passed! Score: ${result.score}/${result.maxScore} 🎉`);
      } else {
        errorToast(`${result.passedTests}/${result.totalTests} tests passed. Score: ${result.score}/${result.maxScore}`);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || "Failed to submit code";
      errorToast(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ✅ Format code using backend formatter
  const handleFormatCode = async () => {
    if (!code.trim()) {
      errorToast("No code to format");
      return;
    }

    setIsFormatting(true);

    try {
      const result = await formatCode({
        language,
        code,
      });

      if (result.success && result.formatted) {
        setCode(result.formatted);
        writeCachedCode(topic?.id, language, result.formatted);
        successToast("Code formatted successfully!");
      } else {
        errorToast(result.error || "Failed to format code");
      }
    } catch (err) {
      const errorMsg = err.message || "Failed to format code";
      errorToast(errorMsg);
    } finally {
      setIsFormatting(false);
    }
  };

  // ✅ Run custom test case
  const handleRunCustom = async () => {
    writeCachedCode(topic?.id, language, code);
    setIsRunning(true);
    setCustomOutput("");
    setCustomError("");

    try {
      const result = await executeCode({
        language,
        version: LANGUAGE_VERSIONS[language] || "1.0.0",
        code,
        stdin: customInput,
      });

      if (result.error) {
        setCustomError(result.error);
      } else {
        setCustomOutput(result.stdout);
        if (result.stderr) {
          setCustomError(result.stderr);
        }
      }
    } catch (err) {
      setCustomError(err.message || "Failed to execute code");
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className={`min-h-screen flex flex-col md:flex-row ${testMode ? "bg-gray-50" : "bg-gray-50"}`}>
      {/* ===== LEFT PANEL (25% test mode / 40% normal) ===== */}
      <div className={`${testMode ? "md:w-1/4 w-full" : "md:w-2/5 w-full"} bg-white border-r border-gray-200 overflow-y-auto transition-all duration-300 ${testMode ? "md:border-b md:border-r-0" : "border-b md:border-b-0 md:border-r"}`}>
        {/* Problem Header */}
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {coding.title || "Coding Problem"}
          </h2>
          <div className="flex items-center gap-2 text-sm">
            <span className={`px-3 py-1 rounded-full font-medium text-white ${coding.difficulty === "easy" ? "bg-green-500" :
              coding.difficulty === "medium" ? "bg-yellow-500" :
                "bg-red-500"
              }`}>
              {coding.difficulty?.charAt(0).toUpperCase() + coding.difficulty?.slice(1) || "Easy"}
            </span>
          </div>
        </div>

        {/* Tabs */}
        {!testMode && (
          <>
            <div className="flex gap-0 border-b border-gray-200">
              {["problem", "submissions"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`flex-1 py-3 text-sm font-medium capitalize border-b-2 transition ${activeTab === tab
                    ? "text-blue-600 border-blue-600"
                    : "text-gray-600 border-transparent hover:text-gray-900"
                    }`}
                >
                  {tab === "submissions" ? `Submissions (${submissions.length})` : tab}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {activeTab === "problem" && (
                <div className="space-y-6">
                  <div className="text-xs text-gray-600">
                    Points: <span className="font-semibold text-gray-900">{bestScore}/{topic?.points ?? 0}</span>
                    {attemptsRemaining < 3 && (
                      <span className="ml-3">Attempts remaining: <span className="font-semibold">{attemptsRemaining}</span></span>
                    )}
                  </div>

                  {/* Problem Statement */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">
                      Problem Statement
                    </h4>
                    <p className="text-gray-700 whitespace-pre-wrap text-sm leading-relaxed">
                      {coding.question}
                    </p>
                  </div>

                  {/* Input Format */}
                  {coding.inputFormat && (
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3">Input Format</h4>
                      <p className="text-gray-700 whitespace-pre-wrap text-sm leading-relaxed">
                        {coding.inputFormat}
                      </p>
                    </div>
                  )}

                  {/* Output Format */}
                  {coding.outputFormat && (
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3">Output Format</h4>
                      <p className="text-gray-700 whitespace-pre-wrap text-sm leading-relaxed">
                        {coding.outputFormat}
                      </p>
                    </div>
                  )}

                  {/* Constraints */}
                  {coding.constraints && coding.constraints.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3">Constraints</h4>
                      <ul className="text-gray-700 text-sm space-y-2">
                        {coding.constraints.map((constraint, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="text-gray-400">•</span>
                            <span>{constraint}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Examples */}
                  {coding.examples && coding.examples.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3">Examples</h4>
                      <div className="space-y-4">
                        {coding.examples.map((example, idx) => (
                          <div
                            key={idx}
                            className="bg-gray-50 p-3 rounded border border-gray-200 text-sm"
                          >
                            <p className="font-medium text-gray-900 mb-2">
                              Example {idx + 1}:
                            </p>
                            <div className="space-y-1 font-mono text-gray-700 text-xs">
                              <p className="whitespace-pre-wrap">
                                <span className="font-medium">Input:</span> {example.input}
                              </p>
                              <p className="whitespace-pre-wrap">
                                <span className="font-medium">Expected Output:</span> {example.expected}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                </div>
              )}

              {activeTab === "submissions" && (
                <div className="space-y-4">
                  {submissions.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      No submissions yet
                    </div>
                  ) : (
                    submissions.map((sub, idx) => {
                      const allPassed = sub.passedTests === sub.totalTests;
                      const submissionId = sub.id || `${sub.submittedAt || ""}_${idx}`;
                      const isExpanded = expandedSubmissionId === submissionId;
                      const submittedCode =
                        typeof sub.code === "string"
                          ? sub.code
                          : typeof sub.submittedCode === "string"
                            ? sub.submittedCode
                            : typeof sub.sourceCode === "string"
                              ? sub.sourceCode
                              : null;
                      return (
                        <div
                          key={submissionId}
                          className={`p-4 rounded-lg border ${allPassed
                            ? "bg-green-50 border-green-200"
                            : "bg-orange-50 border-orange-200"
                            }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-semibold text-gray-900">
                              Attempt #{sub.attemptNumber || idx + 1}
                            </span>
                            <span
                              className={`text-sm font-bold ${allPassed ? "text-green-600" : "text-orange-600"
                                }`}
                            >
                              {allPassed ? "✓ All Passed" : `${sub.passedTests || 0}/${sub.totalTests || 0} Passed`}
                            </span>
                          </div>

                          <div className="flex items-center justify-between mb-2">
                            <div className="text-xs text-gray-600">
                              Language: <span className="font-semibold uppercase">{sub.language}</span>
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                setExpandedSubmissionId((prev) => (prev === submissionId ? null : submissionId))
                              }
                              className="text-xs font-medium text-blue-700 hover:text-blue-800"
                              disabled={!submittedCode}
                              title={submittedCode ? "View submitted code" : "Code not available for this submission"}
                            >
                              {submittedCode ? (isExpanded ? "Hide Code" : "View Code") : "Code Unavailable"}
                            </button>
                          </div>

                          <div className="text-xs text-gray-600 space-y-1">
                            <p>
                              Score: <span className="font-semibold">{sub.score}/{sub.maxScore}</span>
                            </p>
                            <p>
                              Execution Time: <span className="font-semibold">{sub.execTimeMs?.toFixed(2) || 0}ms</span>
                            </p>
                            <p>
                              Submitted: {new Date(sub.submittedAt * 1000).toLocaleString()}
                            </p>
                          </div>

                          {isExpanded && submittedCode && (
                            <div className="mt-3">
                              <div className="text-xs font-semibold text-gray-900 mb-2">Submitted Code</div>
                              <pre className="bg-gray-900 text-gray-100 p-3 rounded-lg overflow-auto text-xs whitespace-pre-wrap">
                                {submittedCode}
                              </pre>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {/* Test Results Display */}
        {testMode && testResults && (
          <div className="p-4">
            <button
              onClick={() => setTestMode(false)}
              className="text-sm text-blue-600 hover:text-blue-700 mb-4"
            >
              ← Back to Code
            </button>

            <div className="space-y-4">
              {/* Visible Tests Status */}
              <div className="border-b border-gray-200 pb-4">
                <p className="text-xs font-semibold text-gray-900 mb-3">Visible Tests</p>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Passed:</span>
                    <span className="font-bold text-green-600">
                      {testResults.testResults?.filter((r) => r.id.includes("visible_test_") && r.passed).length || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Failed:</span>
                    <span className="font-bold text-red-600">
                      {testResults.testResults?.filter((r) => r.id.includes("visible_test_") && !r.passed).length || 0}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                    <div
                      className="h-1.5 rounded-full bg-blue-500"
                      style={{
                        width: `${testResults.testResults?.filter((r) => r.id.includes("visible_test_")).length > 0 ? (testResults.testResults?.filter((r) => r.id.includes("visible_test_") && r.passed).length / testResults.testResults?.filter((r) => r.id.includes("visible_test_")).length) * 100 : 0}%`,
                      }}
                    ></div>
                  </div>
                </div>
              </div>

              {/* Hidden Tests Status */}
              <div>
                <p className="text-xs font-semibold text-gray-900 mb-3">Hidden Tests</p>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Passed:</span>
                    <span className="font-bold text-green-600">
                      {testResults.testResults?.filter((r) => r.id.includes("hidden_test_") && r.passed).length || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Failed:</span>
                    <span className="font-bold text-red-600">
                      {testResults.testResults?.filter((r) => r.id.includes("hidden_test_") && !r.passed).length || 0}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                    <div
                      className="h-1.5 rounded-full bg-blue-500"
                      style={{
                        width: `${testResults.testResults?.filter((r) => r.id.includes("hidden_test_")).length > 0 ? (testResults.testResults?.filter((r) => r.id.includes("hidden_test_") && r.passed).length / testResults.testResults?.filter((r) => r.id.includes("hidden_test_")).length) * 100 : 0}%`,
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ===== MIDDLE PANEL (50%) ===== */}
      <div className={`${testMode ? "md:w-1/2 w-full" : "md:w-3/5 w-full"} flex flex-col bg-white border-r border-gray-200 transition-all duration-300 ${testMode ? "md:border-b md:border-r-0" : "border-b md:border-b-0 md:border-r"}`}>
        {/* Language Selector & Actions Header */}
        <div className="border-b border-gray-200 p-4 flex items-center justify-between">
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {languages.map((lang) => (
              <option key={lang} value={lang}>
                {lang.toUpperCase()}
              </option>
            ))}
          </select>

          <div className="flex gap-2">
            {String(language).toLowerCase() === "go" && (
              <button
                onClick={handleFormatCode}
                disabled={isFormatting || !code.trim()}
                className="px-4 py-2 bg-gray-600 text-white rounded-xl font-medium hover:bg-gray-700 transition-colors shadow-md text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                title="Format code"
              >
                {isFormatting ? "Formatting..." : "Format"}
              </button>
            )}

            <button
              onClick={handleRunTestCases}
              disabled={isTestRunning || !coding.testCases || coding.testCases.length === 0}
              className="ca-btn-primary px-4 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ▶ {isTestRunning ? "Running..." : "Run Tests"}
            </button>

            <button
              onClick={handleSubmitCode}
              disabled={isSubmitting || !canSubmit}
              className="px-4 py-2 bg-orange-600 text-white rounded-xl font-medium hover:bg-orange-700 transition-colors shadow-md text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Submitting..." : canSubmit ? "Submit Code" : "No Attempts Left"}
            </button>
          </div>
        </div>

        {/* Code Editor - Responsive height: tall on mobile, flex on desktop */}
        <textarea
          value={code}
          onChange={(e) => setCode(e.target.value)}
          className="w-full min-h-[60vh] md:min-h-0 md:flex-1 bg-gray-900 text-gray-100 p-4 font-mono text-sm resize-none focus:outline-none"
          spellCheck="false"
        />
      </div>

      {/* ===== RIGHT PANEL (25%) - Test Results & Custom Test ===== */}
      {testMode && testResults && (
        <div className="w-full md:w-1/4 bg-white border-l border-gray-200 md:border-l border-t md:border-t-0 flex flex-col">
          {/* Tabs */}
          <div className="flex gap-0 border-b border-gray-200">
            {["visible", "hidden", "custom"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTestTab(tab)}
                className={`flex-1 py-3 text-xs font-medium capitalize border-b-2 transition ${activeTestTab === tab
                  ? "text-blue-600 border-blue-600"
                  : "text-gray-600 border-transparent hover:text-gray-900"
                  }`}
              >
                {tab === "visible" ? "Visible" : tab === "hidden" ? "Hidden" : "Custom"}
              </button>
            ))}
          </div>

          {/* Test Results Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {activeTestTab === "visible" && (
              <div className="space-y-3">
                {testResults.testResults?.filter(r => r.id.includes("visible_test_")).map((result, idx) => {
                  // Calculate matching prefix for diff highlighting
                  let matchIndex = 0;
                  const exp = result.expected || "";
                  const act = result.actual || "";
                  const minLen = Math.min(exp.length, act.length);

                  for (let i = 0; i < minLen; i++) {
                    if (exp[i] === act[i]) {
                      matchIndex++;
                    } else {
                      break;
                    }
                  }

                  return (
                    <div key={result.id} className="border rounded-lg overflow-hidden">
                      <div
                        className={`p-3 ${result.passed ? "bg-green-50 border-green-300" : "bg-red-50 border-red-300"
                          }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm font-semibold text-gray-900">
                            Test Case {idx + 1}
                          </p>
                          <span
                            className={`text-lg font-bold ${result.passed ? "text-green-600" : "text-red-600"
                              }`}
                          >
                            {result.passed ? "✓" : "✗"}
                          </span>
                        </div>
                        <div className="text-xs space-y-1 font-mono text-gray-700">
                          <div>
                            <span className="font-semibold block mb-1">Input:</span>
                            <pre className="whitespace-pre-wrap bg-white/50 p-1 rounded border border-gray-200">{result.input}</pre>
                          </div>
                          <div>
                            <span className="font-semibold block mb-1">Expected:</span>
                            <pre className="whitespace-pre-wrap bg-white/50 p-1 rounded border border-gray-200">{result.expected}</pre>
                          </div>
                          <div>
                            <span className="font-semibold block mb-1">Actual:</span>
                            <pre className="whitespace-pre-wrap bg-white/50 p-1 rounded border border-gray-200">
                              <span className="bg-green-200 text-green-800">{act.substring(0, matchIndex)}</span>
                              <span className="bg-red-200 text-red-800">{act.substring(matchIndex)}</span>
                            </pre>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            {activeTestTab === "hidden" && (
              <div className="space-y-4">
                {/* Summary Stats */}
                <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                  <p className="text-xs font-semibold text-gray-900 mb-2">Summary</p>
                  <div className="space-y-1 text-xs text-gray-700">
                    <p>
                      <span className="font-medium">Passed:</span>{" "}
                      <span className="text-green-600 font-bold">
                        {testResults.testResults?.filter((r) => r.id.includes("hidden_test_") && r.passed).length || 0}
                      </span>
                    </p>
                    <p>
                      <span className="font-medium">Failed:</span>{" "}
                      <span className="text-red-600 font-bold">
                        {testResults.testResults?.filter((r) => r.id.includes("hidden_test_") && !r.passed).length || 0}
                      </span>
                    </p>
                  </div>
                </div>

                {/* Hidden Test Cases List */}
                <div className="space-y-2">
                  {testResults.testResults
                    ?.filter((r) => r.id.includes("hidden_test_"))
                    .map((result, idx) => (
                      <div key={result.id} className="border rounded-lg overflow-hidden">
                        <div
                          className={`p-3 ${result.passed ? "bg-green-50 border-green-300" : "bg-red-50 border-red-300"
                            }`}
                        >
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-semibold text-gray-900">
                              Test Case {idx + 1}
                            </p>
                            <span
                              className={`text-lg font-bold ${result.passed ? "text-green-600" : "text-red-600"
                                }`}
                            >
                              {result.passed ? "✓" : "✗"}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {activeTestTab === "custom" && (
              <div className="space-y-3 flex flex-col h-full">
                <label className="text-xs font-semibold text-gray-900">
                  Custom Input
                </label>
                <textarea
                  value={customInput}
                  onChange={(e) => setCustomInput(e.target.value)}
                  placeholder="Enter test input here"
                  className="h-24 border border-gray-300 rounded-lg p-2 font-mono text-xs resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                />

                <button
                  onClick={handleRunCustom}
                  disabled={isRunning}
                  className="px-3 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 transition text-xs"
                >
                  ▶ {isRunning ? "Running..." : "Run"}
                </button>

                {(customOutput || customError) && (
                  <div className="border-t border-gray-200 pt-3">
                    <label className="text-xs font-semibold text-gray-900 mb-2 block">
                      Output
                    </label>
                    <div
                      className={`p-2 rounded-lg font-mono text-xs whitespace-pre-wrap overflow-auto max-h-32 ${customError
                        ? "text-red-700 bg-red-50"
                        : "text-green-700 bg-green-50"
                        }`}
                    >
                      {customError ? `Error: ${customError}` : customOutput}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ===== CUSTOM TEST PANEL (Show when NOT in test mode) ===== */}
      {!testMode && (
        <div className="w-full md:w-1/4 md:h-full bg-white border-l border-gray-200 md:border-l border-t md:border-t-0 flex flex-col hidden md:flex overflow-y-auto">
          {/* Tabs */}
          <div className="flex gap-0 border-b border-gray-200">
            {["custom"].map((tab) => (
              <button
                key={tab}
                className="flex-1 py-3 text-xs font-medium capitalize border-b-2 border-blue-600 text-blue-600"
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Custom Test Content */}
          <div className="flex-1 flex flex-col p-4">
            <div className="space-y-3 flex flex-col h-full">
              <label className="text-xs font-semibold text-gray-900">
                Custom Input
              </label>
              <textarea
                value={customInput}
                onChange={(e) => setCustomInput(e.target.value)}
                placeholder="Enter test input here"
                className="h-24 border border-gray-300 rounded-lg p-2 font-mono text-xs resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              />

              <button
                onClick={handleRunCustom}
                disabled={isRunning}
                className="px-3 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 transition text-xs"
              >
                ▶ {isRunning ? "Running..." : "Run"}
              </button>

              {(customOutput || customError) && (
                <div className="border-t border-gray-200 pt-3">
                  <label className="text-xs font-semibold text-gray-900 mb-2 block">
                    Output
                  </label>
                  <div
                    className={`p-2 rounded-lg font-mono text-xs whitespace-pre-wrap overflow-auto max-h-32 ${customError
                      ? "text-red-700 bg-red-50"
                      : "text-green-700 bg-green-50"
                      }`}
                  >
                    {customError ? `Error: ${customError}` : customOutput}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Auth Prompt Modal */}
      <AuthPromptModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        message="Please log in to submit your code."
      />
    </div>
  );
};

export default CodingView;
